import { Box, Button, Grid, Typography } from '@mui/material';
import React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { sizes } from '../../../constans';
import { handleErrorMessages } from '../../../helpers';
import { AdditionalFormData, RequiredFormData, RequiredFormDataWithCaptcha } from '../formTypes';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { theme } from '../../../theme';
import { Input } from './Input';
import { LoaderComponent } from '../LoaderComponent';

type Props = {
  requiredData: RequiredFormData;
  captchaRef: React.MutableRefObject<ReCAPTCHA | undefined>;
  setRequiredData: (data: RequiredFormDataWithCaptcha | null) => void;
  setIsFinished: (isFinished: boolean) => void;
};

export const AdditionalFormFields: React.FC<Props> = ({
  requiredData,
  captchaRef,
  setRequiredData,
  setIsFinished,
}) => {
  const { t } = useTranslation();
  const [isServerError, setServerError] = React.useState(false);

  const { handleSubmit, setError, reset, register, formState: { isSubmitting, errors } } = useForm<AdditionalFormData>({
    defaultValues: {
      companySize: '',
      role: '',
      phone: '',
      sellerId: ''
    },
    mode: 'onChange'
  });

  const onSubmit = React.useCallback(
    async (data: AdditionalFormData) => {
      const token = await captchaRef.current?.executeAsync();
      captchaRef.current?.reset();
      try {
        const postResponse = await window.fetch('/Registration/Register', {
          method: 'POST',
          mode: 'cors',
          headers: {
            'content-type': 'application/json;charset=UTF-8'
          },
          body: JSON.stringify({ ...data, ...requiredData, captcha: token })
        });

        if (postResponse.ok) {
          setRequiredData(null);
          setIsFinished(true);
          reset();
          // https://developers.google.com/tag-platform/tag-manager/web/datalayer#use_a_data_layer_with_event_handlers
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'registrationSuccess',
            companySize: data.companySize
          });
        } else if (postResponse.status === 400) {
          const responseText = JSON.parse(await postResponse.text());
          handleErrorMessages(responseText['errors'], (field, message) => {
            setError(field, { type: 'custom', message: message });
          }, () => setServerError(true));
        } else {
          setServerError(true); 
        }
      } catch (e) {
        console.error(e);
        setServerError(true);
      }
    },
    [setError, reset, requiredData, captchaRef, setRequiredData, setIsFinished]
  );

  return (
    <>
      {isSubmitting && <LoaderComponent />}
      <form onSubmit={handleSubmit(onSubmit)} style={{ visibility : isSubmitting ? 'hidden' : 'visible'}}>
        {isServerError &&
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ErrorOutlineIcon sx={{ mr: '0.5rem' }} color="error" />
            <Typography pb="0px" color={theme.palette.error.main} variant="body2">
              {t('errors.unknownError')}
            </Typography>
          </Box>
        }
        <Typography variant="body2" mb="0.4rem" fontSize={'10px'}>
          {t('infoText.registration')}
        </Typography>
        <Input
          register={register}
          errors={errors}
          name="companySize"
          rules={{ required: false }}
          label={t('fields.size')}
          select
          options={sizes}
        />
        <Input
          register={register}
          errors={errors}
          name="role"
          rules={{ required: false }}
          label={t('fields.role')} 
        />
        <Grid sx={{ margin: 'auto', justifyContent: 'space-between' }} container>
          <Grid sx={{ width: '100%' }} item sm={5.8} md={5.8} lg={5.8}>
            <Input
              register={register}
              errors={errors}
              name="phone"
              rules={{ required: false, pattern: /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-s./0-9]*$/ }}
              label={t('fields.phone')}
            />
          </Grid>
          <Grid sx={{ width: '100%' }} item sm={5.8} md={5.8} lg={5.8}>
            <Input
              register={register}
              errors={errors}
              name="sellerId"
              rules={{ required: false, pattern: /\d+/ }}
              label={t('fields.id')}
            />
          </Grid>
        </Grid>
        <Box
          sx={{
            mt: '1rem',
            maxWidth: '22rem',
            display: 'flex',
            margin: 'auto',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Button
            color="success"
            onClick={handleSubmit(onSubmit)}
            sx={{ maxWidth: '170.125px', backgroundColor: '#fff', textTransform: 'none' }}
            fullWidth
            variant="outlined"
          >
            {t('buttonLabels.skip')}
          </Button>

          <Button
            color="success"
            onClick={handleSubmit(onSubmit)}
            sx={{ maxWidth: '170.125px', color: 'white', textTransform: 'none' }}
            fullWidth
            variant="contained"
          >
            {t('buttonLabels.continue')}
          </Button>
        </Box>
      </form>
    </>
  );
};
